import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Anchor, HardHat, Building2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatError } from "@/context/AuthContext";

export default function AuthModal() {
  const { authOpen, authMode, authPrefillEmail, setAuthOpen, login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState(authMode);
  const [role, setRole] = useState("candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  // sync mode + prefill whenever the modal is (re)opened from any trigger
  useEffect(() => {
    if (authOpen) {
      setMode(authMode);
      setForm((f) => ({ ...f, email: authPrefillEmail || f.email }));
    }
  }, [authOpen, authMode, authPrefillEmail]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        await register({ ...form, role });
        toast.success("Welcome aboard", { description: "Your account is ready." });
      } else {
        await login(form.email, form.password);
        toast.success("Signed in");
      }
      setAuthOpen(false);
      setForm({ name: "", email: "", password: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="max-w-md bg-[#10161F] border-white/10 text-[#F4F6F8] rounded-none p-0 gap-0 [&>button]:hidden">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center bg-[#FF5E1E] text-[#0B0F14]"><Anchor size={18} strokeWidth={2.5} /></span>
            <DialogTitle className="font-display font-black uppercase tracking-tight text-lg">{mode === "register" ? "Create account" : "Sign in"}</DialogTitle>
          </div>
          <button data-testid="auth-modal-close" onClick={() => setAuthOpen(false)} className="text-[#8FA3B8] hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6">
          {/* mode toggle */}
          <div className="grid grid-cols-2 mb-5 border border-white/10">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`auth-tab-${m}`} onClick={() => setMode(m)}
                className={`py-2.5 font-mono-tech text-[10px] tracking-[0.15em] uppercase transition-colors ${mode === m ? "bg-[#FF5E1E] text-[#0B0F14]" : "text-[#8FA3B8] hover:text-white"}`}>
                {m === "login" ? "Sign In" : "Register"}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[{ k: "candidate", I: HardHat, t: "I'm a Worker" }, { k: "employer", I: Building2, t: "I'm an Employer" }].map(({ k, I, t }) => (
                <button key={k} data-testid={`role-select-${k}`} onClick={() => setRole(k)} type="button"
                  className={`flex flex-col items-center gap-1.5 py-3 border transition-colors ${role === k ? "border-[#FF5E1E] bg-[#FF5E1E]/10 text-white" : "border-white/10 text-[#8FA3B8] hover:border-white/30"}`}>
                  <I size={18} /><span className="text-xs font-medium">{t}</span>
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div>
                <Label className="auth-lbl">Full name</Label>
                <Input data-testid="auth-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required className="auth-in" placeholder="John Rivera" />
              </div>
            )}
            <div>
              <Label className="auth-lbl">Email</Label>
              <Input data-testid="auth-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="auth-in" placeholder="you@email.com" />
            </div>
            <div>
              <Label className="auth-lbl">Password</Label>
              <Input data-testid="auth-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required className="auth-in" placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="auth-submit-button" className="w-full rounded-none bg-[#FF5E1E] hover:bg-[#ff7038] text-[#0B0F14] font-display font-bold uppercase h-11">
              {busy ? "Please wait…" : mode === "register" ? "Create Account" : "Sign In"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-4">
            <span className="h-px flex-1 bg-white/10" />
            <span className="font-mono-tech text-[9px] tracking-[0.2em] text-[#8FA3B8]">OR</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <Button onClick={() => loginWithGoogle(role)} data-testid="auth-google-button" variant="outline" className="w-full rounded-none border-white/20 bg-white/5 hover:bg-white/10 text-white h-11 font-medium">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="mr-2 h-4 w-4" /> Continue with Google
          </Button>
          <p className="mt-3 text-[10px] text-[#8FA3B8] text-center font-mono-tech tracking-wide">SECURE SIGN-IN · YOUR PROGRESS IS SAVED</p>
        </div>

        <style>{`
          .auth-lbl { font-family:'JetBrains Mono',monospace; font-size:9px; letter-spacing:0.15em; text-transform:uppercase; color:#8FA3B8; margin-bottom:6px; display:block; }
          .auth-in { background:#0B0F14; border:1px solid rgba(255,255,255,0.14); border-radius:0; color:#F4F6F8; height:2.6rem; }
          .auth-in:focus-visible { outline:none; border-color:#FF5E1E; box-shadow:0 0 0 1px #FF5E1E; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}
